import { CDN_URL, CREATE_SPRITE_WORKER_URL } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { tokenManager } from '../../utils/TokenManager.js';
import { loadOutfitMappings } from '../../utils/outfitMappings.js';
import { HangoutState } from '../state/HangoutState.js';
import { hangoutWorkerUrl, makeWorkerRequest } from './hangout-worker-request.js';
import { buildSpriteCreditLine } from './message-image-prompt-modal.js';
import { showNotification } from './services/spriteUpgradeService.js';

const MODAL_ID = 'message-sprite-reference-modal';

const spriteUrl = (charId, outfitId, actionId) =>
  `${CDN_URL}/s/sprites/${charId}/${outfitId}/${actionId}.webp`;

const ensureModal = () => {
  const existing = document.getElementById(MODAL_ID);
  if (existing) return existing;

  const modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.className = 'message-image-prompt-modal message-sprite-reference-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'message-sprite-reference-title');
  modal.innerHTML = `
    <div class="message-image-prompt-modal__panel">
      <h2 id="message-sprite-reference-title" class="message-image-prompt-modal__title">Create this image</h2>
      <p class="message-image-prompt-modal__status" id="message-sprite-reference-status" aria-live="polite"></p>
      <img id="message-sprite-reference-image" class="message-sprite-reference-modal__image" alt="Existing image for this action" />
      <p id="message-sprite-reference-credit" class="pro-tip-text" aria-live="polite">
        <span class="pro-tip-text__label">Pro tip:</span>
        <span data-credit-line>1 image = 1 credit</span>
      </p>
      <div class="message-image-prompt-modal__actions">
        <button type="button" id="message-sprite-reference-cancel" class="btn" tabindex="0">Cancel</button>
        <button type="button" id="message-sprite-reference-validate" class="btn btn-primary" tabindex="0">Validate</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  return modal;
};

const closeModal = (modal) => {
  modal.classList.remove('show');
  modal.hidden = true;
};

const referenceTitle = async (characterId, outfitId) => {
  const characters = HangoutState.getInstance()?.state?.allCharacters || [];
  const character = characters.find((entry) => String(entry.id) === String(characterId));
  const characterName = String(character?.name || character?.characterName || 'Character').trim() || 'Character';
  const mappings = await loadOutfitMappings();
  const parsedOutfitId = Number(outfitId);
  const outfitName = mappings?.[parsedOutfitId]?.name || 'default outfit';
  return `Create this image for ${characterName} (outfit: ${outfitName})`;
};

export const openMessageSpriteReferenceModal = async ({ characterId, outfitId, actionId }) => {
  const parsedActionId = Number(actionId);
  if (!Number.isInteger(parsedActionId) || parsedActionId <= 0) return;

  const modal = ensureModal();
  const statusEl = modal.querySelector('#message-sprite-reference-status');
  const imageEl = modal.querySelector('#message-sprite-reference-image');
  const creditEl = modal.querySelector('#message-sprite-reference-credit');
  const creditLineEl = creditEl?.querySelector('[data-credit-line]');
  const cancelBtn = modal.querySelector('#message-sprite-reference-cancel');
  const validateBtn = modal.querySelector('#message-sprite-reference-validate');
  if (!(imageEl instanceof HTMLImageElement)) return;
  if (!(cancelBtn instanceof HTMLButtonElement) || !(validateBtn instanceof HTMLButtonElement)) return;

  imageEl.removeAttribute('src');
  validateBtn.disabled = true;
  const titleEl = modal.querySelector('#message-sprite-reference-title');
  if (titleEl) titleEl.textContent = await referenceTitle(characterId, outfitId);
  if (statusEl) statusEl.textContent = 'Finding an existing image...';
  modal.hidden = false;
  modal.classList.add('show');

  const handleCancel = () => closeModal(modal);
  cancelBtn.onclick = handleCancel;
  modal.onclick = (event) => {
    if (event.target === modal) handleCancel();
  };

  let reference = null;
  try {
    const parsedOutfitId = Number(outfitId);
    const response = await makeWorkerRequest(hangoutWorkerUrl('/v1/hangout/message/reference-sprite'), {
      actionId: parsedActionId,
      ...(Number.isInteger(parsedOutfitId) && parsedOutfitId >= 0 ? { outfitId: parsedOutfitId } : {})
    });
    reference = await response.json();
  } catch (error) {
    if (statusEl) statusEl.textContent = '';
    await showNotification(error?.message || 'Could not find an existing image.', 'error');
    closeModal(modal);
    return;
  }

  const sourceCharId = Number(reference?.charId);
  const sourceOutfitId = Number(reference?.outfitId);
  if (!Number.isFinite(sourceCharId) || !Number.isFinite(sourceOutfitId)) {
    if (statusEl) statusEl.textContent = '';
    await showNotification(reference?.error || 'Could not find an existing image.', 'error');
    closeModal(modal);
    return;
  }

  imageEl.src = spriteUrl(sourceCharId, sourceOutfitId, parsedActionId);
  const credits = await buildSpriteCreditLine(characterId);
  if (creditLineEl) creditLineEl.textContent = credits.text;
  creditEl?.classList.toggle('insufficient', !credits.hasEnough);
  validateBtn.disabled = !credits.hasEnough;
  if (statusEl) statusEl.textContent = '';

  validateBtn.onclick = async () => {
    validateBtn.disabled = true;
    if (statusEl) statusEl.textContent = 'Creating image...';
    try {
      const token = await tokenManager.getValidAccessToken();
      if (!token) throw new Error('No valid access token');
      await fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/schedule-sprites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: {
          character_id: characterId,
          outfit_id: outfitId ?? 0,
          sprites: [{ action_id: parsedActionId }]
        }
      });
      await showNotification('Image scheduled for creation!', 'success');
      closeModal(modal);
    } catch (error) {
      const message = error?.status === 409
        ? 'Image already exists'
        : (error?.body?.error || error?.body?.message || error?.message || 'Failed to create image');
      await showNotification(message, 'error');
      validateBtn.disabled = !credits.hasEnough;
      if (statusEl) statusEl.textContent = '';
    }
  };
};
